import { useMemo, useState } from "react";
import { Card } from "@/components/layout";
import {
  MetricKpiTile,
  PanelError,
  PanelLoading,
  SectionHeading,
  panelErrorTitle,
} from "@/components/dashboard";
import { DonutChart, StackedBar, type StackedSegment } from "@/components/charts";
import type { BarTone } from "@/components/charts";
import { EmptyState } from "@/components/feedback";
import { useQmFriction } from "@/hooks/useQmFriction";
import type { TimeRange } from "@/lib/time-range";
import type { FrictionPage } from "@/lib/validation";

interface QmFrictionSectionProps {
  range: TimeRange;
}

interface FrictionDimension {
  key: keyof Pick<
    FrictionPage,
    "rageClicks" | "errorClicks" | "frustration" | "rageScrolls" | "disabledInputClicks"
  >;
  label: string;
  tone: BarTone;
}

// Single source of truth for the five QM friction signals, ordered worst-weighted
// first. Reused by the per-row StackedBar and the composition donut.
const FRICTION_DIMENSIONS: readonly FrictionDimension[] = [
  { key: "rageClicks", label: "Rage clicks", tone: "red" },
  { key: "errorClicks", label: "JS-error clicks", tone: "amber" },
  { key: "frustration", label: "Frustration", tone: "sky" },
  { key: "rageScrolls", label: "Rage scrolls", tone: "slate" },
  { key: "disabledInputClicks", label: "Disabled-input", tone: "emerald" },
] as const;

function pageSegments(page: FrictionPage): StackedSegment[] {
  return FRICTION_DIMENSIONS.map((d) => ({
    label: d.label,
    value: page[d.key],
    tone: d.tone,
  }));
}

export function QmFrictionSection({ range }: QmFrictionSectionProps) {
  const qm = useQmFriction({ from: range.from, to: range.to });
  const err = qm.error ? panelErrorTitle(qm.error) : undefined;

  const [hideLowVolume, setHideLowVolume] = useState(false);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);

  const pages = useMemo(() => qm.data?.pages ?? [], [qm.data]);
  const excludedLowVolume = qm.data?.excludedLowVolume ?? 0;

  const visiblePages = useMemo(
    () => (hideLowVolume ? pages.filter((p) => !p.lowVolume) : pages),
    [pages, hideLowVolume],
  );

  const summary = useMemo(() => {
    const worst = pages[0];
    return {
      worst,
      totalFriction: pages.reduce((acc, p) => acc + p.totalFriction, 0),
      sessionsAffected: pages.reduce((acc, p) => acc + p.sessionsAffected, 0),
      pageCount: pages.length,
    };
  }, [pages]);

  const maxStruggle = useMemo(
    () => Math.max(1, ...visiblePages.map((p) => p.struggleScore)),
    [visiblePages],
  );

  const donutSlices = useMemo(
    () =>
      FRICTION_DIMENSIONS.map((d) => ({
        label: d.label,
        tone: d.tone,
        value: visiblePages.reduce((acc, p) => acc + p[d.key], 0),
      })),
    [visiblePages],
  );

  const selected = selectedPath
    ? pages.find((p) => p.path === selectedPath)
    : undefined;

  return (
    <section className="flex flex-col gap-3">
      <SectionHeading
        title="User Friction"
        frs={["A1"]}
        source="bigquery"
        description="Most-frustrating pages from QuantumMetric session replay — rage clicks, JS-error clicks, and frustration signals, ranked by a weighted struggle score per 1k pageviews."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricKpiTile
          label="Worst struggle score"
          value={summary.worst ? summary.worst.struggleScore.toLocaleString("en-US") : "—"}
          delta={summary.worst?.path}
          tone="red"
          hint="Highest weighted-friction-per-1k-pageviews score in the range. Higher is worse. Rage clicks and JS errors weigh most."
          isLoading={qm.isLoading}
          errorTitle={err}
        />
        <MetricKpiTile
          label="Total friction events"
          value={summary.totalFriction.toLocaleString("en-US")}
          delta="Across all tracked pages"
          tone="amber"
          hint="Sum of the five friction signals (rage clicks, JS-error clicks, frustration, rage scrolls, disabled-input clicks) over every ranked page."
          isLoading={qm.isLoading}
          errorTitle={err}
        />
        <MetricKpiTile
          label="Pages with friction"
          value={summary.pageCount.toLocaleString("en-US")}
          delta={
            excludedLowVolume > 0
              ? `${excludedLowVolume.toLocaleString("en-US")} low-traffic hidden`
              : undefined
          }
          tone="neutral"
          hint="Number of pages returned in the ranked list. Low-traffic pages below the minimum-pageviews threshold are excluded server-side."
          isLoading={qm.isLoading}
          errorTitle={err}
        />
        <MetricKpiTile
          label="Sessions affected"
          value={summary.sessionsAffected.toLocaleString("en-US")}
          delta="Distinct sessions with friction"
          tone="sky"
          hint="Total distinct sessions that hit at least one friction signal across the ranked pages."
          isLoading={qm.isLoading}
          errorTitle={err}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card
          title="Most frustrating pages"
          className="lg:col-span-2"
          action={
            <button
              type="button"
              onClick={() => setHideLowVolume((v) => !v)}
              aria-pressed={hideLowVolume}
              className={`rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 transition-colors ${
                hideLowVolume
                  ? "bg-warning/15 text-warning-text ring-warning/30"
                  : "text-fg-muted ring-border-subtle hover:bg-surface-raised/60"
              }`}
            >
              {hideLowVolume ? "Showing high-traffic only" : "Hide small samples"}
            </button>
          }
        >
          {qm.isLoading ? (
            <PanelLoading />
          ) : qm.error ? (
            <PanelError error={qm.error} onRetry={() => qm.refetch()} />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="text-[11px] font-medium uppercase tracking-wide text-fg-muted">
                      <th scope="col" className="px-2 py-2">Page</th>
                      <th scope="col" className="px-2 py-2">Struggle</th>
                      <th scope="col" className="px-2 py-2 w-32">Friction mix</th>
                      <th scope="col" className="px-2 py-2 text-right">Views</th>
                      <th scope="col" className="px-2 py-2 text-right">Sessions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visiblePages.length === 0 ? (
                      <EmptyState
                        columns={5}
                        message="No friction recorded for this range."
                      />
                    ) : (
                      visiblePages.map((page) => {
                        const isSelected = page.path === selectedPath;
                        return (
                          <tr
                            key={page.path}
                            role="button"
                            tabIndex={0}
                            onClick={() => setSelectedPath(page.path)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setSelectedPath(page.path);
                              }
                            }}
                            className={`cursor-pointer border-t border-border-subtle transition-colors hover:bg-surface-raised/40 ${
                              isSelected ? "bg-surface-raised/60 ring-1 ring-info/30" : ""
                            }`}
                          >
                            <td className="px-2 py-2">
                              <div className="flex items-center gap-2">
                                <span
                                  className="max-w-[180px] truncate text-neutral-text"
                                  title={page.path}
                                >
                                  {page.path}
                                </span>
                                {page.lowVolume && (
                                  <span className="inline-flex shrink-0 items-center rounded-full bg-warning/15 px-1.5 py-0.5 text-[10px] font-medium text-warning-text ring-1 ring-warning/30">
                                    small sample
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-2 py-2">
                              <div className="flex items-center gap-2">
                                <span className="tabular-nums font-medium text-neutral-text-strong">
                                  {page.struggleScore.toLocaleString("en-US")}
                                </span>
                                <span className="hidden h-1.5 w-16 overflow-hidden rounded-full bg-danger/10 sm:block">
                                  <span
                                    className="block h-full rounded-full bg-danger/70"
                                    style={{
                                      width: `${(page.struggleScore / maxStruggle) * 100}%`,
                                    }}
                                  />
                                </span>
                              </div>
                            </td>
                            <td className="px-2 py-2">
                              <StackedBar segments={pageSegments(page)} height={8} />
                            </td>
                            <td className="px-2 py-2 text-right tabular-nums text-neutral-text">
                              {page.pageviews.toLocaleString("en-US")}
                            </td>
                            <td className="px-2 py-2 text-right tabular-nums text-neutral-text">
                              {page.sessionsAffected.toLocaleString("en-US")}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              {excludedLowVolume > 0 && (
                <p className="mt-3 text-[11px] text-fg-faint">
                  {excludedLowVolume.toLocaleString("en-US")} low-traffic pages hidden
                  (below the minimum-pageviews threshold).
                </p>
              )}
            </>
          )}
        </Card>

        <Card title="Friction composition">
          {qm.isLoading ? (
            <PanelLoading />
          ) : qm.error ? (
            <PanelError error={qm.error} onRetry={() => qm.refetch()} />
          ) : (
            <DonutChart
              slices={donutSlices}
              centerLabel="Friction mix"
              emptyMessage="No friction in range"
            />
          )}
        </Card>
      </div>

      {selected && (
        <Card title={`Watch these sessions — ${selected.path}`}>
          {selected.replayLinks.length > 0 ? (
            <ul className="flex flex-wrap gap-2">
              {selected.replayLinks.map((link, i) => (
                <li key={link}>
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border-subtle bg-surface-inset/60 px-3 py-1.5 text-xs text-info-text transition-colors hover:bg-surface-raised/60"
                  >
                    <span
                      className="h-1.5 w-1.5 rounded-full bg-info/70"
                      aria-hidden="true"
                    />
                    Session {i + 1}
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <div className="flex flex-col gap-2">
              <p className="text-xs text-fg-faint">
                {selected.sessionsAffected.toLocaleString("en-US")} sessions affected.
                Replay deep-links unavailable (QM_REPLAY_BASE_URL not configured on
                the backend).
              </p>
              {selected.sampleSessionIds.length > 0 && (
                <ul className="flex flex-wrap gap-1.5">
                  {selected.sampleSessionIds.map((id) => (
                    <li
                      key={id}
                      className="rounded border border-border-subtle bg-surface-inset/60 px-2 py-0.5 font-mono text-[11px] text-fg-disabled"
                    >
                      {id}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </Card>
      )}
    </section>
  );
}
